import indexesService from '@/services/amio/indexes.service'
import { INDEX_TYPES } from '@/utils/bot-json.constants'
import { isProductFeedIndex } from '@/utils/index.utils'

// PRO-740: a few real products from the answer's knowledge base, as the cards the bot sends — for the canvas
// preview of a product-recommendation answer. Asked once per knowledge base and shared by every bubble; a failed or
// empty answer is forgotten so the next render tries again, and the preview falls back to its sample cards meanwhile.
const PREVIEW_QUERY = 'products'
const PREVIEW_COUNT = 3

// ponytail: a minute is short enough that a feed created in another tab shows up without a reload, long enough
// that the add menu on every node of the canvas shares one request
const FEED_CHECK_TTL_MS = 60 * 1000

let cache = new Map()
let feedCheck = null

export function resetPreviewProducts() {
  cache = new Map()
  feedCheck = null
}

// Whether the organisation has a knowledge base with a product feed — without one a product answer cannot be
// saved. Fails open: the engine still refuses to save such an answer, so a failed check must not hide the option.
export function hasProductFeed() {
  if (!feedCheck || Date.now() - feedCheck.at > FEED_CHECK_TTL_MS) {
    const check = {
      at: Date.now(),
      result: indexesService.list().then(indexes => indexes.some(isProductFeedIndex)).catch(() => true)
    }
    // "no feed" is not kept: a feed created meanwhile must be found by the very next check
    check.result.then(connected => { if (!connected && feedCheck === check) feedCheck = null })
    feedCheck = check
  }
  return feedCheck.result
}

export function loadPreviewProducts({ indexId, indexType, marketId = null, language = 'en' }) {
  if (!indexId) return Promise.resolve([])

  const key = [indexType, indexId, marketId, language].join(':')
  if (!cache.has(key)) {
    const request = fetchCards({ indexId, indexType, marketId, language })
      .then(cards => {
        // nothing yet (a feed still being indexed) is not an answer to keep — the next preview asks again
        if (!cards.length) cache.delete(key)
        return cards
      })
      .catch(() => {
        cache.delete(key)
        return []
      })
    cache.set(key, request)
  }
  return cache.get(key)
}

async function fetchCards({ indexId, indexType, marketId, language }) {
  const searchIndexId = indexType === INDEX_TYPES.GROUP ? await resolveGroupIndexId(indexId, marketId) : indexId
  if (!searchIndexId) return []

  const response = await indexesService.searchKnowledgeBase(searchIndexId, {
    query: PREVIEW_QUERY, topN: PREVIEW_COUNT, mode: 'products', language
  })
  return response?.cards || []
}

// The search endpoint is per knowledge base; a market group previews the bot's own market, else its first one
async function resolveGroupIndexId(groupId, marketId) {
  const groupIndexes = (await indexesService.list()).filter(index => index.index_group_id === groupId)
  const marketIndex = groupIndexes.find(index => index.market_id === marketId)
  return (marketIndex || groupIndexes[0])?.id || null
}
